"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink } from "lucide-react";

interface OAuthReauthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountingSystem: string;
  venueId: string | number;
  errorMessage?: string;
  userRoleId?: number; // 1 = super_admin, 2 = booking_staff
}

export default function OAuthReauthDialog({
  open,
  onOpenChange,
  accountingSystem,
  venueId,
  errorMessage,
  userRoleId,
}: OAuthReauthDialogProps) {
  const isSuperAdmin = userRoleId === 1;
  const isBookingStaff = userRoleId === 2;
  const handleReauthorize = () => {
    const authUrl = `/api/oauth/${accountingSystem}/authorize?venueId=${venueId}`;

    // Open OAuth window
    const width = 800;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const authWindow = window.open(
      authUrl,
      "oauth_window",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
    );

    // Poll for window closure
    const checkWindow = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkWindow);
        onOpenChange(false);
        // Reload the page to retry the operation with new credentials
        window.location.reload();
      }
    }, 500);
  };

  const systemDisplayName =
    accountingSystem === "fortnox" ? "Fortnox" : "e-conomic";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className="text-yellow-600">⚠️</span>
            Authorization Required
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              Your {systemDisplayName} connection has expired or is no longer
              valid.
            </p>
            {errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700 font-mono">{errorMessage}</p>
              </div>
            )}

            {isBookingStaff ? (
              <>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm text-amber-800">
                    <strong>Administrator Action Required</strong>
                    <br />
                    <br />
                    Only system administrators can re-authorize the{" "}
                    {systemDisplayName} connection.
                    <br />
                    <br />
                    Please contact your administrator to refresh the accounting
                    system credentials.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p>
                  To continue using accounting features, please reconnect to{" "}
                  {systemDisplayName}.
                </p>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>What happens next:</strong>
                    <br />
                    1. A popup window will open to {systemDisplayName}
                    <br />
                    2. Log in and authorize the connection
                    <br />
                    3. This page will refresh with the new credentials
                  </p>
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          {(isSuperAdmin || !userRoleId) && (
            <AlertDialogAction onClick={handleReauthorize} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Reconnect to {systemDisplayName}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
